import { useState } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useCdrs } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { formatDuration, formatMoney, formatDateTime, timezoneAbbr } from '../../lib/format.js';

export function CdrsPage() {
  const tenantId = useSession((s) => s.activeTenantId);
  const session = useSession((s) => s.session);
  const [direction, setDirection] = useState('');

  // Tenant timezone, never the browser's -- a call at 09:00 in Windhoek must
  // not read as 10:00 because the admin is in Johannesburg (doc 07 §3).
  const tz = session?.memberships[0]?.tenant_id ? 'Africa/Johannesburg' : 'Africa/Johannesburg';

  const { data, isLoading, error } = useCdrs(tenantId, {
    direction: direction || undefined, limit: 100,
  });

  if (!tenantId) {
    return <Empty icon="⬢" title="No tenant selected" hint="Choose a tenant to view its call history." />;
  }

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Call history</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Last 30 days · times in {timezoneAbbr(tz)}
          </p>
        </div>
      </div>

      {error && <ErrorBanner error={error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <select
            className="arc-select" style={{ width: 'auto' }}
            value={direction} onChange={(e) => setDirection(e.target.value)}
            aria-label="Filter by direction"
          >
            <option value="">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </select>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {data ? `${data.data.length} calls` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Direction</th>
                <th>From</th>
                <th>To</th>
                <th className="arc-td-right">Duration</th>
                <th>Quality</th>
                <th className="arc-td-right">Cost</th>
              </tr>
            </thead>
            {isLoading ? (
              <TableSkeleton cols={7} />
            ) : (
              <tbody>
                {data?.data.map((c) => (
                  <tr key={c.id}>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(c.started_at, tz)}
                    </td>
                    <td>
                      <Badge tone={c.direction === 'outbound' ? 'info' : c.direction === 'inbound' ? 'ok' : 'muted'}>
                        {c.direction}
                      </Badge>
                    </td>
                    <td className="arc-mono">{c.from_number ?? '—'}</td>
                    <td className="arc-mono">{c.to_number ?? c.destination_e164 ?? '—'}</td>
                    <td className="arc-td-right arc-num">
                      {c.answered_at ? formatDuration(c.duration_sec) : <span className="arc-subtle">no answer</span>}
                    </td>
                    <td>{c.mos ? <MosIndicator mos={c.mos} /> : <span className="arc-subtle">—</span>}</td>
                    <td className="arc-td-right arc-num">{formatMoney(c.sell_amount, c.currency)}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!isLoading && data?.data.length === 0 && (
          <Empty
            icon="☎"
            title="No calls yet"
            hint="Call records appear here once the switch ships them. Dial 9196 from a registered handset to generate one."
          />
        )}
      </div>
    </>
  );
}

/** MOS with a label as well as a colour, so it does not rely on hue alone. */
function MosIndicator({ mos }: { mos: string }) {
  const v = Number(mos);
  const tone = v >= 4.0 ? 'ok' : v >= 3.5 ? 'warn' : 'danger';
  const label = v >= 4.0 ? 'good' : v >= 3.5 ? 'fair' : 'poor';
  return (
    <Badge tone={tone}>
      <span className="arc-dot" aria-hidden="true" />
      {mos} {label}
    </Badge>
  );
}
